import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getAdminPermissionsByName } from "./roleAndPermissionActions";
import { RootState } from "../../store/store";
import RoleAndPermissionStyles from "./RoleAndPermissionStyles";

const Permissions = () => {
  // Use API data
  const { adminRoleData, permissionData } = useAppSelector(
    (state: RootState) => state.adminRoles
  );
  const roles = useMemo(
    () => adminRoleData.rolePermissionsResponses,
    [adminRoleData]
  );
  const [selectedRole, setSelectedRole] = useState(roles[0]);
  const [search, setSearch] = useState("");
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (selectedRole?.name) {
      dispatch(getAdminPermissionsByName(selectedRole.name));
    }
  }, [selectedRole, dispatch]);

  // Find the selected role in adminRoleData to get createdAt/updatedAt
  const selectedRoleFromData = useMemo(
    () =>
      adminRoleData.rolePermissionsResponses.find(
        (role) => role.name === selectedRole.name
      ),
    [adminRoleData, selectedRole]
  );

  // Map permissionData to table rows, adding createdAt/updatedAt from role
  const mappedPermissions = useMemo(() => {
    if (!permissionData?.length || !Array.isArray(permissionData)) return [];
    const createdAt = selectedRoleFromData?.createdAt
      ? new Date(selectedRoleFromData.createdAt).toLocaleDateString()
      : "-";
    const updatedAt = selectedRoleFromData?.updatedAt
      ? new Date(selectedRoleFromData.updatedAt).toLocaleDateString()
      : "-";
    return permissionData?.map((perm: any) => ({
      name: perm.key,
      description: perm.description,
      createdAt,
      updatedAt,
    }));
  }, [permissionData, selectedRoleFromData]);

  // Filter permissions by search
  const filteredPermissions = mappedPermissions.filter((perm) =>
    perm.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box
      id="permissions_container"
      data-test-id="test-permissions_container"
      aria-label="Permissions Container"
    >
      <Grid
        container
        spacing={2}
        sx={RoleAndPermissionStyles.permissionsCardGrid}
        id="permissions_grid"
        data-test-id="test-permissions_grid"
        aria-label="Permissions Cards Grid"
      >
        {roles?.map((role) => (
          <Grid item xs={12} sm={6} md={4} key={role.name}>
            <Paper
              onClick={() => setSelectedRole(role)}
              sx={RoleAndPermissionStyles.permissionsRoleCard(
                selectedRole.name === role.name
              )}
              id={`permissions_role_card_${role.name}`}
              data-test-id={`test-permissions_role_card_${role.name}`}
              aria-label={`Role Card for ${role.name}`}
            >
              <Typography
                sx={RoleAndPermissionStyles.permissionsRoleName}
                id={`permissions_role_name_${role.name}`}
                data-test-id={`test-permissions_role_name_${role.name}`}
                aria-label={`Role Name: ${role.name}`}
              >
                {role.name}
              </Typography>
              <Box
                sx={RoleAndPermissionStyles.permissionsRoleInfo}
                id={`permissions_role_info_${role.name}`}
                data-test-id={`test-permissions_role_info_${role.name}`}
                aria-label={`Role Information for ${role.name}`}
              >
                <Typography
                  variant="body2"
                  id={`permissions_permissions_count_${role.name}`}
                  data-test-id={`test-permissions_permissions_count_${role.name}`}
                >
                  {role.permissionsCount} Permissions
                </Typography>
                <Typography
                  variant="body2"
                  id={`permissions_total_users_${role.name}`}
                  data-test-id={`test-permissions_total_users_${role.name}`}
                >
                  {role.totalUsersPerRole} Users
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box
        sx={RoleAndPermissionStyles.permissionsHeaderBox}
        id="permissions_header_box"
        data-test-id="test-permissions_header_box"
        aria-label="Permissions Header Box"
      >
        <Typography
          variant="h6"
          sx={RoleAndPermissionStyles.permissionsTitle}
          id="permissions_title"
          data-test-id="test-permissions_title"
          aria-label={`Permissions for ${selectedRole?.name}`}
        >
          Permissions for {selectedRole?.name}
        </Typography>
        <TextField
          placeholder="Search permissions..."
          size="small"
          sx={RoleAndPermissionStyles.permissionsSearchField}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          id="permissions_search_input"
          data-test-id="test-permissions_search_input"
          aria-label="Search Permissions Input"
        />
      </Box>

      <TableContainer
        component={Paper}
        id="permissions_table_container"
        data-test-id="test-permissions_table_container"
        aria-label="Permissions Table Container"
      >
        <Table
          id="permissions_table"
          data-test-id="test-permissions_table"
          aria-label="Permissions Table"
        >
          <TableHead>
            <TableRow>
              <TableCell
                id="permissions_header_name"
                data-test-id="test-permissions_header_name"
                aria-label="Permission Name Header"
              >
                Permission Name
              </TableCell>
              <TableCell
                id="permissions_header_description"
                data-test-id="test-permissions_header_description"
                aria-label="Description Header"
              >
                Description
              </TableCell>
              <TableCell
                id="permissions_header_created_date"
                data-test-id="test-permissions_header_created_date"
                aria-label="Created Date Header"
              >
                Created Date
              </TableCell>
              <TableCell
                id="permissions_header_last_modified"
                data-test-id="test-permissions_header_last_modified"
                aria-label="Last Modified Header"
              >
                Last Modified
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPermissions.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  align="center"
                  aria-label="No Permissions Found"
                >
                  No permissions found.
                </TableCell>
              </TableRow>
            ) : (
              filteredPermissions?.map((perm) => (
                <TableRow
                  key={perm.name}
                  id={`permissions_row_${perm.name}`}
                  data-test-id={`test-permissions_row_${perm.name}`}
                  aria-label={`Permission Row for ${perm.name}`}
                >
                  <TableCell
                    id={`permissions_name_${perm.name}`}
                    data-test-id={`test-permissions_name_${perm.name}`}
                    aria-label={`Permission Name: ${perm.name}`}
                  >
                    {perm.name}
                  </TableCell>
                  <TableCell
                    id={`permissions_description_${perm.name}`}
                    data-test-id={`test-permissions_description_${perm.name}`}
                    aria-label={`Permission Description: ${perm.description}`}
                  >
                    {perm.description}
                  </TableCell>
                  <TableCell
                    id={`permissions_created_at_${perm.name}`}
                    data-test-id={`test-permissions_created_at_${perm.name}`}
                    aria-label={`Permission Created At: ${perm.createdAt}`}
                  >
                    {perm.createdAt}
                  </TableCell>
                  <TableCell
                    id={`permissions_updated_at_${perm.name}`}
                    data-test-id={`test-permissions_updated_at_${perm.name}`}
                    aria-label={`Permission Last Modified: ${perm.updatedAt}`}
                  >
                    {perm.updatedAt}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Permissions;
